import { isAxiosError } from "axios"
import { useCallback, useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type {
  Pagination,
  Reconciliation,
  ReconciliationFormValues,
  ReconciliationListItem,
} from "../types"
import { toStringOrNull } from "src/features/admin/screen-shared"

export const RECONCILIATIONS_ENDPOINT = `${SMOOTH_SMOOTHER_API_URL}/screen/reconciliations`

export const useReconciliations = (page: number) => {
  const { getWithToken } = useSmoothApi()
  const { data, error, isLoading } = useSWR(
    `${RECONCILIATIONS_ENDPOINT}?page=${page}`,
    async (url: string) =>
      (await getWithToken(url)).data as {
        reconciliations: ReconciliationListItem[]
        pagination: Pagination
      },
    { revalidateOnFocus: false }
  )

  return {
    reconciliations: data?.reconciliations ?? [],
    pagination: data?.pagination ?? null,
    error: error ?? null,
    isLoading,
  }
}

export const useReconciliation = (id: number | null) => {
  const { getWithToken } = useSmoothApi()
  const { data, error, isLoading, mutate } = useSWR(
    id ? `${RECONCILIATIONS_ENDPOINT}/${id}` : null,
    async (url: string) => (await getWithToken(url)).data as { reconciliation: Reconciliation },
    { revalidateOnFocus: false }
  )

  return { reconciliation: data?.reconciliation ?? null, error: error ?? null, isLoading, mutate }
}

export const toMessage = (e: unknown, fallback: string) =>
  isAxiosError(e) && typeof e.response?.data?.message === "string"
    ? e.response.data.message
    : fallback

const toRequestBody = (values: ReconciliationFormValues) => ({
  paymentId: toStringOrNull(values.paymentId),
  paymentPromiseId: toStringOrNull(values.paymentPromiseId),
  invoiceableType: values.invoiceableType,
  invoiceableId: toStringOrNull(values.invoiceableId),
  amount: toStringOrNull(values.amount),
})

type SaveResult<T> = { ok: true; value: T } | { ok: false; message: string }

export const useSaveReconciliation = () => {
  const { postWithToken, patchWithToken, deleteWithToken } = useSmoothApi()
  const [isSaving, setIsSaving] = useState(false)

  const run = useCallback(
    async <T>(request: () => Promise<T>, fallback: string): Promise<SaveResult<T>> => {
      setIsSaving(true)
      try {
        return { ok: true, value: await request() }
      } catch (e) {
        return { ok: false, message: toMessage(e, fallback) }
      } finally {
        setIsSaving(false)
      }
    },
    []
  )

  const create = useCallback(
    (values: ReconciliationFormValues) =>
      run(async () => {
        const response = await postWithToken(RECONCILIATIONS_ENDPOINT, toRequestBody(values))
        return (response.data as { id: number }).id
      }, "作成に失敗しました"),
    [postWithToken, run]
  )

  const update = useCallback(
    (id: number, values: ReconciliationFormValues) =>
      run(
        () => patchWithToken(`${RECONCILIATIONS_ENDPOINT}/${id}`, toRequestBody(values)),
        "保存に失敗しました"
      ),
    [patchWithToken, run]
  )

  const destroy = useCallback(
    (id: number) =>
      run(() => deleteWithToken(`${RECONCILIATIONS_ENDPOINT}/${id}`), "削除に失敗しました"),
    [deleteWithToken, run]
  )

  return { create, update, destroy, isSaving }
}
